// ============================================
// APP: NVA (React App) + funzione serverless api/events.ts
// FILE: src/lib/eventsParser.ts
// PURPOSE: La lettura delle pagine eventi di iltaccodibacco.it, in un solo
//   posto per il telefono (EventsContext, il ripiego via proxy) e per il
//   server (api/events.ts). Prima stava dentro EventsContext.
//   Niente React e niente i18n: deve girare anche in Node.
//   Titolo, date, città, immagine, descrizione e link si estraggono come
//   prima, e l'id resta quello deterministico di sempre.
// ============================================

import * as cheerio from 'cheerio';

/** Un evento come esce dalla pagina del sito (senza "nuovo" né distanza). */
export interface ScrapedEvent {
  id: string;
  title: string;
  startDate: Date;
  endDate?: Date;
  city: string;
  description?: string;
  link?: string;
  imageUrl?: string;
}

/** La pagina già letta da cheerio: il server la riusa per altri controlli. */
export type EventsPage = ReturnType<typeof cheerio.load>;

// Ordinati per lunghezza decrescente, così "Mola di Bari" vince su "Bari".
export const KNOWN_CITIES = [
  'Acquaviva delle Fonti', 'Sannicandro di Bari', 'Sammichele di Bari',
  'Gravina in Puglia', 'Santeramo in Colle', 'Polignano a Mare',
  'Castellana Grotte', 'Gioia del Colle', 'Ruvo di Puglia',
  'Palo del Colle', 'Grumo Appula', 'Mola di Bari', 'Locorotondo',
  'Conversano', 'Alberobello', 'Casamassima', 'Noicattaro', 'Giovinazzo',
  'Putignano', 'Rutigliano', 'Triggiano', 'Valenzano', 'Terlizzi',
  'Monopoli', 'Altamura', 'Bitonto', 'Modugno', 'Adelfia', 'Bitritto',
  'Capurso', 'Bitetto', 'Corato', 'Fasano', 'Noci', 'Turi', 'Bari',
];

const MONTHS_IT: Record<string, number> = {
  'gennaio': 0, 'febbraio': 1, 'marzo': 2, 'aprile': 3,
  'maggio': 4, 'giugno': 5, 'luglio': 6, 'agosto': 7,
  'settembre': 8, 'ottobre': 9, 'novembre': 10, 'dicembre': 11,
};

// ─── Identità stabile di un evento ───
// Un evento è identificato da titolo + giorno di calendario. Da questa
// chiave si ricava un id DETERMINISTICO, così lo stesso evento tiene lo
// stesso id fra una lettura e l'altra, nelle cache in memoria e in
// localStorage, e ora anche fra server (api/events) e telefono.
//
// Perché conta: preferiti (`nva-saved-events`) e promemoria
// (`nva-event-reminders`) sono salvati per id. Un tempo l'id era casuale
// (`Date.now() + Math.random()`) e ogni nuova lettura li scollegava. La
// chiave usa il GIORNO (non l'ora), così resta la stessa anche dopo che
// "enrich times" aggiunge l'ora a startDate: prima diffEvents() segnava
// come "nuovi" eventi già visti.

/** "2026-09-23": il giorno di calendario dai campi locali della data. */
export function dayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Chiave unica di "stesso evento", usata da id, doppioni e confronto dei nuovi. */
export function makeEventKey(title: string, startDate: Date): string {
  return `${title.trim().toLowerCase()}__${dayKey(startDate)}`;
}

export function makeEventId(title: string, startDate: Date): string {
  // Hash djb2 (32 bit) della chiave: corto, stabile e abbastanza sicuro
  // contro le collisioni per le poche centinaia di eventi che teniamo.
  const key = makeEventKey(title, startDate);
  let hash = 5381;
  for (let i = 0; i < key.length; i++) {
    hash = (Math.imul(hash, 33) + key.charCodeAt(i)) | 0;
  }
  return `evt_${(hash >>> 0).toString(36)}`;
}

// Le date "da ... a ..." del sito:
//   "dal 21 al 23 settembre 2026"            (stesso mese, come sempre)
//   "dall'11 al 24 settembre 2026 in mostra" (dall' davanti a 1, 8, 11)
//   "dal 10 ottobre al 30 novembre 2026"     (mesi diversi)
//   "dal 2 ottobre 2026 al 10 gennaio 2027"  (anni diversi)
// Prima si leggeva solo il primo caso: gli altri diventavano un giorno solo
// (spesso l'ULTIMO), e una mostra "dal 10 ottobre al 30 novembre" finiva al
// 30 novembre invece di essere "in corso".
const RANGE_RE =
  /dal(?:l['’]\s*|\s+)(\d{1,2})(?:\s+([a-z]+))?(?:\s+(\d{4}))?\s+al(?:l['’]\s*|\s+)(\d{1,2})\s+([a-z]+)\s+(\d{4})/i;

function parseDateRange(text: string): { startDate: Date; endDate: Date } | null {
  const m = text.match(RANGE_RE);
  if (!m) return null;
  const endMonthStr = m[5].toLowerCase();
  if (!MONTHS_IT.hasOwnProperty(endMonthStr)) return null;
  const endMonth = MONTHS_IT[endMonthStr];
  const endYear = parseInt(m[6]);

  let startMonth = endMonth;
  if (m[2]) {
    const startMonthStr = m[2].toLowerCase();
    if (!MONTHS_IT.hasOwnProperty(startMonthStr)) return null;
    startMonth = MONTHS_IT[startMonthStr];
  }
  // Senza anno d'inizio: lo stesso della fine, o quello prima se il mese viene dopo ("dal 28 dicembre al 6 gennaio 2027").
  const startYear = m[3] ? parseInt(m[3]) : startMonth > endMonth ? endYear - 1 : endYear;

  const startDate = new Date(startYear, startMonth, parseInt(m[1]));
  const endDate = new Date(endYear, endMonth, parseInt(m[4]));
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return null;
  return { startDate, endDate };
}

/**
 * Tutti gli eventi di una pagina (o di un pezzo "carica eventi successivi"),
 * nell'ordine della pagina e senza filtri di data; i doppioni della stessa
 * pagina sono già tolti.
 * `defaultCity` vale quando nel luogo non si riconosce nessuna città.
 */
export function extractEvents($: EventsPage, defaultCity: string): ScrapedEvent[] {
  const uniqueEvents = new Map<string, ScrapedEvent>();

  $('.evento-featured').each((_, element) => {
    const titleEl = $(element).find('.titolo.blocco-locali h2 a');
    const title = titleEl.text().trim();
    const link = titleEl.attr('href');
    const dateText = $(element).find('.testa').text().trim();
    // Prima il testo del link (più preciso), poi tutto il luogo per la città
    const locationLink = $(element).find('.evento-data a').text().trim();
    const locationFull = $(element).find('.evento-data').text().trim();
    const description = $(element).find('.evento-corpo').text().trim();

    // Immagine dell'evento
    let imageUrl: string | undefined;
    const imgEl = $(element).find('img');
    if (imgEl.length) {
      const src = imgEl.attr('src') || imgEl.attr('data-src');
      if (src) {
        imageUrl = src.startsWith('http') ? src : `https://iltaccodibacco.it${src.startsWith('/') ? '' : '/'}${src}`;
      }
    }

    let city = defaultCity;
    // Prima nel testo del link (più preciso)
    for (const c of KNOWN_CITIES) {
      if (locationLink.includes(c)) { city = c; break; }
    }
    // Se non c'è, in tutto il luogo (che contiene anche la provincia)
    if (city === defaultCity && locationFull) {
      for (const c of KNOWN_CITIES) {
        if (locationFull.includes(c)) { city = c; break; }
      }
    }

    const range = parseDateRange(dateText);
    let startDate: Date | undefined = range ? range.startDate : undefined;
    const endDate: Date | undefined = range ? range.endDate : undefined;
    const singleDateMatch = dateText.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/);

    if (!range && singleDateMatch) {
      const day = parseInt(singleDateMatch[1]);
      const monthStr = singleDateMatch[2].toLowerCase();
      const year = parseInt(singleDateMatch[3]);
      if (MONTHS_IT.hasOwnProperty(monthStr)) {
        startDate = new Date(year, MONTHS_IT[monthStr], day);
      }
    }

    if (title && startDate && !isNaN(startDate.getTime())) {
      const key = makeEventKey(title, startDate);
      if (!uniqueEvents.has(key)) {
        uniqueEvents.set(key, {
          id: makeEventId(title, startDate),
          title,
          startDate,
          endDate,
          city,
          description,
          link,
          imageUrl,
        });
      }
    }
  });

  return Array.from(uniqueEvents.values());
}

/**
 * Come prima in EventsContext: gli eventi della pagina ordinati per data,
 * solo quelli futuri o in corso (giorno del telefono), al massimo `limit`.
 */
export function parseEventsFromHtml(html: string, defaultCity: string, limit?: number): ScrapedEvent[] {
  const allEvents = extractEvents(cheerio.load(html), defaultCity);
  allEvents.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const futureEvents = allEvents.filter((event) => {
    const eventEndDate = event.endDate || event.startDate;
    return eventEndDate >= now;
  });

  return limit ? futureEvents.slice(0, limit) : futureEvents;
}
